
import Vue from "vue";
import RadialApiClient from "@/api/RadialApiClient";
import { ProductCustomValueSetting, CustomValueType } from "@/api/entities";
import { CreateProductCustomValueSettingDto, UpdateProductCustomValueSettingDto } from "@/api/dto";
import ConfirmationDialog from "@/components/vuetify/ConfirmationDialog.vue";
import Pagination from "@/components/vuetify/Pagination.vue";
import { CustomValueTypeItem, User } from "@/store/model";
import CustomSelector from "@/components/vuetify/Selector.vue";
import CustomTextField from "@/components/vuetify/TextField.vue";
import { Constant } from "@/store/constant";
import * as lodash from "lodash";
import mixin from "@/mixin/mixin";

export interface DataType {
  alertMessage: string;
  alertType: string;
  showingAlert: boolean;
  activeItem: ProductCustomValueSetting | null;
  selectedIndex: number;
  productCustomValueSettings: ProductCustomValueSetting[];
  newProductCustomValueSetting: {
    settingName: string;
    settingType: CustomValueType | null;
  };
  dialogAddProductCustomValueSetting: boolean;
  dialogEditProductCustomValueSetting: boolean;
  dialogDeleteProductCustomValueSetting: boolean;
  selectedProductCustomValueSetting: ProductCustomValueSetting | null;
  page: number;
  pageSize: number;
  list: ProductCustomValueSetting[];
  listCount: number;
  customTypeValues: CustomValueTypeItem[];
}

export default Vue.extend({
  name: "SkuCustom",
  mixins: [mixin],
  components: {
    ConfirmationDialog,
    Pagination,
    CustomSelector,
    CustomTextField,
  },
  data(): DataType {
    return {
      alertMessage: "",
      alertType: "success",
      showingAlert: false,
      activeItem: null,
      selectedIndex: 0,
      productCustomValueSettings: [],
      newProductCustomValueSetting: { settingName: "", settingType: null },
      dialogAddProductCustomValueSetting: false,
      dialogEditProductCustomValueSetting: false,
      dialogDeleteProductCustomValueSetting: false,
      selectedProductCustomValueSetting: null,
      page: 1,
      pageSize: 12,
      list: [],
      listCount: 0,
      customTypeValues: Constant.customValueTypes,
    };
  },
  props: {
    user: {
      type: Object as () => User | null,
      required: true,
    },
  },
  computed: {
    editProductCustomValueSettingDisabled(): boolean {
      if (this.selectedProductCustomValueSetting) {
        if (this.selectedProductCustomValueSetting.name === "" || this.isProhibited) {
          // 項目名が空文字列 or 禁止文字が使用されている場合
          return true;
        }
        if (
          this.productCustomValueSettings
            .filter((_, index) => index !== this.selectedIndex)
            .some((productCustomValueSetting) => {
              return productCustomValueSetting.name === this.selectedProductCustomValueSetting?.name;
            })
        ) {
          // 同じ項目名のものがすでにある場合
          return true;
        }
        if (
          lodash.isEqual(this.selectedProductCustomValueSetting, this.productCustomValueSettings[this.selectedIndex])
        ) {
          // 値が変更されていない場合
          return true;
        }
        return false;
      }
      return true;
    },
    addProductCustomValueSettingDisabled(): boolean {
      // コード、名前が空の場合、禁止文字列が使用されている場合
      if (
        this.newProductCustomValueSetting.settingType === null ||
        this.newProductCustomValueSetting.settingName === "" ||
        this.isProhibited
      ) {
        return true;
      }
      // 同じ項目名のものがすでにある場合
      if (
        this.productCustomValueSettings.some((productCustomValueSetting) => {
          return productCustomValueSetting.name === this.newProductCustomValueSetting.settingName;
        })
      ) {
        return true;
      }
      return false;
    },
    isProhibited() {
      const prohibitedChars = /\\|\/|:|\*|\?|"|<|>|\||_/;
      if (prohibitedChars.test(this.newProductCustomValueSetting.settingName)) {
        return true;
      } else if (this.selectedProductCustomValueSetting?.name) {
        return prohibitedChars.test(this.selectedProductCustomValueSetting.name);
      } else {
        return false;
      }
    },
  },
  methods: {
    listActive(item: ProductCustomValueSetting) {
      if (this.activeItem === item) {
        this.activeItem = null;
      } else {
        this.activeItem = item;
      }
    },
    menuChanged(isActive: boolean) {
      if (!isActive) {
        this.activeItem = null;
      }
    },
    selectProductCustomValueSetting(
      ProductCustomValueSetting: ProductCustomValueSetting,
      index: number,
      selectedIndex: number
    ) {
      this.selectedProductCustomValueSetting = { ...ProductCustomValueSetting };
      if (index === 0) {
        this.selectedIndex = selectedIndex;
        this.dialogEditProductCustomValueSetting = true;
      } else {
        this.dialogDeleteProductCustomValueSetting = true;
      }
    },
    deselectProductCustomValueSetting() {
      this.selectedProductCustomValueSetting = null;
      this.dialogEditProductCustomValueSetting = false;
      this.dialogDeleteProductCustomValueSetting = false;
    },
    sortList() {
      this.list.sort((a, b) => {
        if (a.id > b.id) {
          return 1;
        } else {
          return -1;
        }
      });
    },
    async addProductCustomValueSetting() {
      this.$emit("isLoading", true);

      if (this.user && this.newProductCustomValueSetting.settingType) {
        let isError = false;
        const dto: CreateProductCustomValueSettingDto = {
          company: { connect: { id: this.user?.companyId } },
          type: this.newProductCustomValueSetting.settingType,
          name: this.newProductCustomValueSetting.settingName,
        };
        const createdProductCustomValueSetting = await RadialApiClient.createProductCustomValueSetting(dto);
        if (createdProductCustomValueSetting) {
          this.list.push(createdProductCustomValueSetting);
        } else {
          isError = true;
        }
        if (isError) {
          this.showAlert(
            `エラーが発生しております。
                入力内容をご確認のうえ、時間を空けてからもう一度お試しください。`,
            "error"
          );
        } else {
          this.listCount = this.list.length;
          this.sortList();
          this.updatePage(this.page);
          this.showAlert("新しくカスタム項目が作成されました！", "success");
        }
      }
      this.newProductCustomValueSetting = { settingName: "", settingType: null };
      this.$emit("isLoading", false);
      this.dialogAddProductCustomValueSetting = false;
    },
    cancelAddProductCustomValueSetting() {
      this.dialogAddProductCustomValueSetting = false;
      this.newProductCustomValueSetting = { settingName: "", settingType: null };
    },
    async editProductCustomValueSetting() {
      this.$emit("isLoading", true);
      if (this.selectedProductCustomValueSetting) {
        const dto: UpdateProductCustomValueSettingDto = {
          name: this.selectedProductCustomValueSetting.name,
          type: this.selectedProductCustomValueSetting.type,
        };
        await RadialApiClient.updateProductCustomValueSetting(this.selectedProductCustomValueSetting.id, dto);
        const list = await RadialApiClient.listProductCustomValueSettings();
        if (list) {
          this.list = list;
          this.sortList();
          this.updatePage(this.page);
        }
        this.selectedProductCustomValueSetting = null;
        this.dialogEditProductCustomValueSetting = false;
      }
      this.$emit("isLoading", false);
    },
    async deleteProductCustomValueSetting() {
      this.dialogDeleteProductCustomValueSetting = false;
      this.$emit("isLoading", true);
      if (this.selectedProductCustomValueSetting) {
        await RadialApiClient.deleteProductCustomValueSetting(this.selectedProductCustomValueSetting.id);
        const list = await RadialApiClient.listProductCustomValueSettings();
        if (list) {
          this.list = list;
          this.listCount = this.list.length;
          this.sortList();
          this.updatePage(this.page);
        }
      }
      this.selectedProductCustomValueSetting = null;
      this.$emit("isLoading", false);
      this.showAlert("無事に削除が完了しました！", "success");
    },
    getAlertMessage(): string {
      if (this.selectedProductCustomValueSetting) {
        return `本当に${this.selectedProductCustomValueSetting.name}を削除してもよろしいですか？
                設定されている値が全て削除されます。`;
      } else {
        return "";
      }
    },
    showAlert(message: string, type: string) {
      this.alertMessage = message;
      this.alertType = type;
      this.showingAlert = true;
    },
    updatePage(event: number) {
      const start = (event - 1) * this.pageSize;
      const end = event * this.pageSize;
      this.productCustomValueSettings = this.list.slice(start, end);
      this.page = event;
    },
  },
  async mounted() {
    this.$emit("isLoading", true);
    const list = await RadialApiClient.listProductCustomValueSettings();
    if (list) {
      this.list = list;
      this.sortList();
      this.listCount = this.list.length;
      if (this.listCount < this.pageSize) {
        this.productCustomValueSettings = this.list;
      } else {
        this.productCustomValueSettings = this.list.slice(0, this.pageSize);
      }
    }
    this.$emit("isLoading", false);
  },
});
